// frontend/packages/business-console/src/utils/mediaToken.js
//
// Helpers for the `cld:<public_id>` token format used to store media
// references in content frontmatter.
//
//   - toCldToken(publicId)   → "cld:<publicId>"
//   - fromCldToken(token)    → "<publicId>" (strips "cld:" prefix if present)
//   - buildCloudinaryUrl(...) → Cloudinary delivery URL for thumbnails/previews

export function toCldToken(publicId) {
  const pid = String(publicId || "").trim();
  if (!pid) return "";
  return pid.startsWith("cld:") ? pid : `cld:${pid}`;
}

export function fromCldToken(tokenOrPublicId) {
  const v = String(tokenOrPublicId || "").trim();
  if (!v) return "";
  return v.startsWith("cld:") ? v.slice(4) : v;
}

/**
 * Build a Cloudinary delivery URL for a given public id.
 *
 * Used by MediaField and other components that need to render a thumbnail
 * from a stored `cld:` token. The cloud name is expected to come from
 * MediaConfigContext (which loads it from `/v2/media/cloudinary/config`).
 *
 * Returns an empty string if either the public id or cloud name is missing
 * — callers should treat that as "no thumbnail available" and render a
 * placeholder.
 *
 * @param {string} publicIdOrToken  - Token or raw public id ("cld:foo/bar" or "foo/bar").
 * @param {string} cloudName        - Cloudinary cloud name (required).
 * @param {Object} [opts]
 * @param {number} [opts.width]     - Target width; triggers f_auto/q_auto/c_fill transforms.
 * @param {number} [opts.height]    - Target height.
 * @param {"image"|"video"} [opts.mediaType="image"]
 * @returns {string} Delivery URL or empty string.
 */
export function buildCloudinaryUrl(publicIdOrToken, cloudName, opts = {}) {
  const pid = fromCldToken(publicIdOrToken);
  const cloud = String(cloudName || "").trim();
  if (!pid || !cloud) return "";

  const { width, height, mediaType = "image" } = opts;
  const resource = mediaType === "video" ? "video" : "image";

  const transforms = [];
  if (width || height) {
    transforms.push("c_fill");
    if (width) transforms.push(`w_${width}`);
    if (height) transforms.push(`h_${height}`);
    transforms.push("f_auto", "q_auto");
  }

  const tx = transforms.length ? `${transforms.join(",")}/` : "";
  return `https://res.cloudinary.com/${cloud}/${resource}/upload/${tx}${pid}`;
}
